import { Link, useLocation } from 'react-router-dom'
import { Anchor, Badge, Group, Table, Text } from '@mantine/core'
import { describeSource, isAddressable } from '@racedash/shared'
import type { Event } from '../data/catalog'

/**
 * The event list, shared by the calendar and both vendor pages.
 *
 * A table rather than cards: every event carries the same five facts, and a
 * table lets them be compared down a column instead of re-read on each row.
 * That is what a catalogue is for.
 *
 * Previously this lived in the Calendar route and was imported from there by
 * the vendor pages, which made a route the owner of a shared component.
 */
export default function EventTable({ events, limit }: { events: Event[]; limit?: number }) {
  // Where "back" should lead is a property of the journey, not of the event —
  // the same row appears on three pages. Recording it on the link is what lets
  // the detail page return you to the list you were actually reading.
  const location = useLocation()
  const from = `${location.pathname}${location.search}`
  const shown = limit ? events.slice(0, limit) : events

  return (
    <Table.ScrollContainer minWidth={720}>
      <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Event</Table.Th>
            <Table.Th w={90}>Vendor</Table.Th>
            <Table.Th w={140}>Circuit</Table.Th>
            <Table.Th w={150}>Dates</Table.Th>
            <Table.Th w={170}>Addressing</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {shown.map((event) => {
            const ready = event.source ? isAddressable(event.source) : false
            const vendor = event.source?.vendor ?? 'unknown'
            return (
              <Table.Tr key={event.id}>
                <Table.Td>
                  <Anchor
                    component={Link}
                    to={`/events/${event.id}`}
                    state={{ from }}
                    fw={500}
                    c="inherit"
                    underline="hover"
                  >
                    {event.name}
                  </Anchor>
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" color={vendor === 'ris' ? 'racedash' : 'teal'} size="sm">
                    {vendor}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {event.trackId}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {formatRange(event)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {/*
                    Still a status rather than an error, but it now only ever
                    means a TSL event with no id or a RIS XML event with no
                    path. RIS JSON events are always capturable: capture sweeps
                    the channels rather than being told an address.
                  */}
                  <Group gap="xs" wrap="nowrap">
                    <Badge
                      variant="light"
                      color={ready ? 'green' : 'yellow'}
                      size="sm"
                      // Without this the badge shrinks inside the cell and
                      // ellipsises to "NEEDS …", which reads as a truncated
                      // sentence rather than a status.
                      style={{ flex: 'none' }}
                    >
                      {ready ? 'ready' : 'no address'}
                    </Badge>
                    <Text size="xs" c="dimmed" ff="monospace" truncate>
                      {event.source ? describeSource(event.source) : '—'}
                    </Text>
                  </Group>
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  )
}

function formatRange(event: Event): string {
  const start = event.startsAt?.toDate?.()
  const end = event.endsAt?.toDate?.()
  if (!start) return ''
  const opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    timeZone: event.timezone || 'UTC',
  }
  const s = start.toLocaleDateString('en-GB', opts)
  if (!end) return s
  const e = end.toLocaleDateString('en-GB', { ...opts, year: 'numeric' })
  return s === e.replace(/ \d{4}$/, '') ? e : `${s} – ${e}`
}
